import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export const Route = createFileRoute("/aplicativos-iptv/assist-plus")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o Assist Plus na Samsung, LG e Roku TV" },
      { name: "description", content: "Guia completo de como baixar, instalar e configurar o Assist Plus na sua Smart TV Samsung, LG ou Roku TV. Aplicativo otimizado para Painel IPTV Pós Pago." },
    ],
  }),
  component: AssistPlusTutorial,
});

function AssistPlusTutorial() {
  const tutorialSteps = [
    {
      title: "Samsung Smart TV",
      steps: [
        "Conecte sua Samsung Smart TV à internet.",
        "Acesse a loja de aplicativos Samsung.",
        "Utilize a barra de pesquisa.",
        "Digite: Assist Plus",
        "Selecione o aplicativo oficial.",
        "Clique em Instalar.",
        "Aguarde a conclusão da instalação.",
        "Abra o aplicativo.",
        "Insira os dados de acesso fornecidos pelo seu revendedor."
      ]
    },
    {
      title: "LG Smart TV",
      steps: [
        "Conecte sua LG Smart TV à internet.",
        "Abra the LG Content Store.",
        "Utilize a busca da loja.",
        "Pesquise por: Assist Plus",
        "Selecione o aplicativo.",
        "Clique em Instalar.",
        "Aguarde a instalação ser concluída.",
        "Abra o aplicativo.",
        "Informe seus dados de acesso."
      ]
    },
    {
      title: "Roku TV",
      steps: [
        "Acesse a tela inicial da Roku TV.",
        "Entre em: Streaming Channels",
        "Utilize a busca da Roku Store.",
        "Digite: Assist Plus",
        "Selecione o aplicativo oficial.",
        "Clique em: Adicionar Canal",
        "Aguarde a instalação.",
        "Abra o aplicativo.",
        "Informe suas credenciais de acesso."
      ]
    }
  ];

  return (
    <SEOPage
      title="Como Baixar e Instalar o Assist Plus na Samsung, LG e Roku TV"
      subtitle="O Assist Plus é um aplicativo moderno desenvolvido para oferecer praticidade, desempenho e uma experiência intuitiva em Smart TVs."
      category="Tutorial de Aplicativos"
      introParagraphs={[
        "O Assist Plus é um aplicativo desenvolvido para oferecer praticidade, navegação intuitiva e excelente compatibilidade com Smart TVs. Disponível para Samsung, LG e Roku TV, o aplicativo permite uma experiência moderna e otimizada para quem busca facilidade de acesso e bom desempenho.",
        "Neste tutorial você aprenderá como baixar e instalar o Assist Plus passo a passo em sua televisão. O Assist Plus funciona perfeitamente com sistemas profissionais de Painel IPTV Pós Pago, sendo uma excelente alternativa para revendedores e usuários que procuram qualidade e simplicidade."
      ]}
      breadcrumbs={[
        { label: "Início", href: "/" },
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "Assist Plus", href: "/aplicativos-iptv/assist-plus" }
      ]}
      benefits={[
        { title: "Interface Moderna", description: "Design limpo e intuitivo para fácil navegação entre canais e filmes." },
        { title: "Fácil Instalação", description: "Processo simplificado nas principais lojas de aplicativos de Smart TVs." },
        { title: "Alta Performance", description: "Otimizado para carregar conteúdos rapidamente sem travamentos." },
        { title: "Compatibilidade", description: "Funciona perfeitamente em Samsung, LG e dispositivos Roku." },
        { title: "Configuração Rápida", description: "Basta inserir os dados do seu revendedor para começar a assistir." },
        { title: "Painel Pós Pago", description: "Total integração com sistemas de revenda IPTV pós-pago." }
      ]}
      howItWorks={[
        { title: "Busca", description: "Pesquise pelo aplicativo na loja oficial da sua televisão." },
        { title: "Instalação", description: "Clique para baixar e instalar em poucos segundos." },
        { title: "Acesso", description: "Abra o app e insira suas credenciais de usuário." },
        { title: "Concluído", description: "Aproveite a melhor experiência em sua Smart TV." }
      ]}
      diferenciais={[
        { title: "Estabilidade Garantida", description: "Servidores robustos que garantem transmissão contínua." },
        { title: "Atualizações Constantes", description: "App sempre atualizado com novas funções e melhorias." },
        { title: "Suporte Total", description: "Equipe técnica pronta para auxiliar na configuração." },
        { title: "Multi-Plataforma", description: "Disponível nas maiores marcas de televisores do mercado." }
      ]}
      faq={[
        { question: "O Assist Plus funciona na Samsung Smart TV?", answer: "Sim. O aplicativo está disponível para modelos Samsung compatíveis na loja oficial de aplicativos." },
        { question: "O Assist Plus funciona na LG Smart TV?", answer: "Sim. O aplicativo pode ser encontrado e baixado diretamente na LG Content Store." },
        { question: "O Assist Plus funciona na Roku TV?", answer: "Sim. O aplicativo está disponível na Roku Channel Store como um canal de streaming." },
        { question: "Preciso de login para utilizar o Assist Plus?", answer: "Sim. Após a instalação, basta inserir os dados de acesso fornecidos pelo seu revendedor IPTV." },
        { question: "O Assist Plus é compatível com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo funciona perfeitamente com sistemas profissionais de Painel IPTV Pós Pago." }
      ]}
      sections={[
        {
          title: "Interface do Assist Plus",
          type: "default",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p className="text-lg">
                  Visualize a interface moderna e organizada do Assist Plus. O aplicativo foi projetado para facilitar o acesso aos seus conteúdos favoritos, com categorias bem definidas e uma navegação extremamente fluida.
                </p>
                <ul className="space-y-4">
                  {[
                    "Organização automática de canais e VOD",
                    "Sistema de busca inteligente por títulos",
                    "Design adaptável para diferentes tamanhos de TV",
                    "Interface limpa com foco no conteúdo"
                  ].map((text, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                      <span className="font-medium text-sm uppercase tracking-wider">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-electric/10 blur-[80px] rounded-full animate-pulse-glow" />
                <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl">
                  <img 
                    src="https://wogunbzijppmeuleitjq.supabase.co/storage/v1/object/public/temp-images/cd55c3b0-b507-4652-a1f9-e05aaf00f35d.jpeg" 
                    alt="Interface do Aplicativo Assist Plus" 
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
                </div>
              </motion.div>
            </div>
          )
        },
        {
          title: "Guia de Instalação por Dispositivo",
          type: "grid",
          content: (
            <>
              {tutorialSteps.map((device, idx) => (
                <div key={idx} className="p-8 rounded-3xl bg-white/[0.02] border border-white/5 h-full">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <span className="bg-electric/10 p-2 rounded-lg text-electric text-sm">{idx + 1}</span>
                    {device.title}
                  </h3>
                  <ul className="space-y-4">
                    {device.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="flex gap-3 text-muted-foreground text-sm leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-electric shrink-0 mt-1" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )
        }
      ]}
      ctaTitle="🚀 Tenha Seu Próprio Painel IPTV Pós Pago"
      ctaDescription="Comece hoje mesmo com uma estrutura profissional pronta para escalar suas vendas e oferecer o Assist Plus para seus clientes."
    />
  );
}
